// src/components/ui/LockedBanner.js
//
// ============================================================================
// SUBSCRIPTION STATE BANNER
// ============================================================================
//
// The one place the app tells someone their account cannot save.
//
// Mounted once in App.js rather than per page. A locked customer should learn
// this on whatever screen they happen to open, not only if they wander onto
// the billing tab — and a banner that appears everywhere is one component
// instead of thirty.
//
// TWO STATES, DELIBERATELY DIFFERENT IN TONE
//
//   grace   Payment is failing, or the paid period just ended. They CAN still
//           work. Amber, informative, and it states the deadline — the goal is
//           to prompt an action while there is still time to take it, not to
//           alarm someone whose card merely expired.
//
//   locked  Grace has run out. They CANNOT save. Red, unambiguous, and it says
//           plainly that the work is still there. "Your subscription has
//           ended" alone reads as data loss and generates support tickets;
//           "your work is still here" is both true and the reason to come back.
//
//   notice  A free-access period has ended — a student whose course code ran
//           out. They CAN still work, within the free plan's limits. Nothing
//           has lapsed, nothing is owed, and no card was ever taken. Blue,
//           quiet, and dismissible.
//
//           It exists because this is the one free user who needs telling.
//           Everyone else on the free plan chose it or never had anything
//           else; this person had full access yesterday and will otherwise
//           meet the order cap with no idea why.
//
// active and ordinary free render nothing. Most sessions are one of those, and
// a banner that appears when everything is fine trains people to ignore
// banners.
//
// TWO SURFACES, TWO JOBS
//   This banner is a STATUS INDICATOR. Same words, same colour, the whole
//   session. It answers "what can I do right now" and is meant to fade into
//   the furniture, which is fine — nobody needs to re-read it.
//
//   The per-action explanation is somebody else's job. When a save is refused,
//   the component's own error box says "changes can't be saved" using the
//   refusal wording from the 402 body (entitlementRefusalMessage on the
//   server, surfaced through err.message by the api.js interceptor). That
//   arrives at the exact moment it is needed, in the exact place the user is
//   looking.
//
//   An earlier design had this banner change colour and wording on each
//   refusal. Dropped: a banner that changes is a thing you have to watch, and
//   the modal already says it better and closer to the click.
//
// STICKY, AND ABOVE MODALS
//   `sticky top-0` keeps it visible after scrolling without taking it out of
//   flow, so no spacer is needed and nothing slides underneath.
//
//   z-[60] is deliberate and load-bearing: modal overlays in this app are
//   `fixed inset-0 ... z-50`. At z-50 or below the banner would be covered by
//   any open modal — exactly when someone is being told their save failed and
//   most needs the standing explanation of why.
//
// DISMISSAL IS PER SESSION, NOT PERSISTENT
//   sessionStorage, not localStorage. Someone mid-bid can clear the strip to
//   see their screen; a new session shows it again, because the situation has
//   not changed. Persisting the dismissal would mean the last warning before
//   lockout could be silenced forever with one click.
//
//   The locked state cannot be dismissed at all. There is no work to get back
//   to — every save will fail — so hiding the explanation only produces
//   mystery errors.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useEntitlement from '../../hooks/useEntitlement.js';

const DISMISS_KEY = 'qb_entitlement_banner_dismissed';

const LockedBanner = () => {
  const {
    isLocked, isGrace, message, periodEnd, graceEndsAt, reason,
    resubscribeUrl, isLoading,
  } = useEntitlement();

  // A FREE-ACCESS PERIOD ENDING IS NOT A LAPSE, so it arrives as state FREE
  // and neither isLocked nor isGrace is set. Identified by reason alone.
  //
  // Reading the reason rather than adding an isNotice to the hook keeps this
  // contained: the server already sends it, and every other consumer of the
  // hook is unaffected.
  const isNotice = reason === 'free_access_ended';

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      // Private browsing, or storage disabled. Showing the banner is the safe
      // failure — it is the message that matters, not the dismissal.
      return false;
    }
  });

  // Nothing to say yet, or nothing to say at all. Rendering during load would
  // flash a warning at a customer whose account is perfectly fine.
  if (isLoading) return null;
  if (!isLocked && !isGrace && !isNotice) return null;

  // Grace and the notice are dismissible; locked is not.
  if ((isGrace || isNotice) && dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Dismissal simply does not persist. Acceptable.
    }
  };

  const styles = isNotice
    ? {
        // BLUE, NOT AMBER. Amber says "act before something bad happens" and
        // nothing bad is happening — they are a free user in good standing.
        wrap: 'bg-blue-600 dark:bg-blue-700',
        text: 'text-white',
        sub: 'text-blue-100',
        button: 'bg-white text-blue-700 hover:bg-blue-50',
      }
    : isLocked
    ? {
        wrap: 'bg-red-600 dark:bg-red-700',
        text: 'text-white',
        sub: 'text-red-100',
        button: 'bg-white text-red-700 hover:bg-red-50',
      }
    : {
        wrap: 'bg-amber-500 dark:bg-amber-600',
        text: 'text-white',
        sub: 'text-amber-50',
        button: 'bg-white text-amber-700 hover:bg-amber-50',
      };

  // WHICH DATE, AND WHAT IT MEANS.
  //
  // Both grace reasons arrive as state 'grace', and until now both rendered
  // graceEndsAt under "Changes stay available until". That is right for one of
  // them and wrong for the other.
  //
  //   grace_window            The paid period has ENDED and the seven-day
  //                           cushion is running. graceEndsAt is a genuine
  //                           deadline and "stay available until" is accurate.
  //
  //   paid_period_remaining   Cancelled, but still inside time they have
  //                           already paid for — possibly a year of it.
  //                           graceEndsAt overstates by seven days, and
  //                           worse, "changes stay available until" turns
  //                           months of owned access into a countdown. The
  //                           date that matters is periodEnd.
  //
  //   payment_retrying        A card is failing mid-period. Neither date is a
  //                           promise worth printing — Stripe's retry schedule
  //                           decides when this ends, not us. The message from
  //                           the server already says what to do, so no date.
  //
  // Falls back to the old behaviour when reason is absent, so an older server
  // that does not send it still renders as before rather than losing the line.
  const isPaidThrough = reason === 'paid_period_remaining';
  const dateSource = isPaidThrough ? periodEnd : graceEndsAt;
  const showDate = reason !== 'payment_retrying';

  // Include the year whenever it is not this one.
  //
  // Formatting month and day alone made "August 17, 2027" render as
  // "August 17", which reads as a deadline this week rather than one a year
  // out. A yearly subscriber who cancels keeps access until their paid period
  // ends — often many months away — and telling them it expires in days is
  // both wrong and alarming.
  //
  // Omitting the year is right for a near date and wrong for a far one, so it
  // is decided per date rather than fixed.
  const bannerDate = (() => {
    if (!dateSource) return null;
    const d = new Date(dateSource);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  })();

  // Where the button goes, and it is not the same place for both states.
  //
  //   grace   they already have a subscription — the problem is a card that
  //           declined or a period that lapsed. Account > Billing is where the
  //           payment method and the resubscribe controls actually live.
  //           Sending them to /pricing would ask them to choose a plan they
  //           are already on.
  //
  //   locked  the subscription is gone. There is nothing to manage, so they do
  //           need to pick a plan.
  //   notice  no subscription to manage and nothing to fix. The only thing
  //           they might want is a plan, and only if they now want one — so
  //           the button is an invitation, not an instruction.
  const actionUrl = isNotice
    ? resubscribeUrl
    : isLocked
    ? resubscribeUrl
    : '/account?tab=billing';

  return (
    <div
      className={`w-full sticky top-0 z-[60] shadow-sm ${styles.wrap}`}
      role="status"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <svg
            className={`h-5 w-5 flex-shrink-0 mt-0.5 ${styles.text}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isNotice
                // An information circle. A warning triangle would say
                // something is wrong, and nothing is.
                ? 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                : isLocked
                ? 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                : 'M12 9v2m0 4h.01M5.07 19H19a2 2 0 001.75-2.96l-6.93-12a2 2 0 00-3.5 0l-6.93 12A2 2 0 005.07 19z'}
            />
          </svg>

          <div className="min-w-0">
            <p className={`text-sm font-medium ${styles.text}`}>
              {message || (isNotice
                ? 'Your free access period has ended. Your account is on the free plan.'
                : isLocked
                ? 'Your subscription has ended. Your work is still here — resubscribe to make changes.'
                : 'There is a problem with your subscription.')}
            </p>

            {/* The single rendering of the date, in the reader's own
                timezone. The server used to format it too, and the two
                disagreed by a day across the UTC boundary.

                The wording tracks the date — see the reason branch above.
                "Stay available until" is a deadline and belongs only where
                one exists. */}
            {/* No date line for the notice. Grace and locked are about a
                deadline; a free-access period that has already ended has no
                deadline left, and printing the date it finished only invites
                the reader to work out how long they have been downgraded. */}
            {isGrace && showDate && bannerDate && (
              <p className={`text-xs mt-0.5 ${styles.sub}`}>
                {isPaidThrough
                  ? `Your paid period runs through ${bannerDate}.`
                  : `Changes stay available until ${bannerDate}.`}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={actionUrl}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${styles.button}`}
          >
            {isNotice ? 'See plans' : isLocked ? 'Resubscribe' : 'Manage billing'}
          </Link>

          {/* Locked has no dismiss. Every save will fail, so hiding the reason
              only converts a clear explanation into mystery errors. */}
          {isGrace && (
            <button
              type="button"
              onClick={handleDismiss}
              className={`p-1.5 rounded-md hover:bg-white/20 transition-colors ${styles.text}`}
              aria-label="Dismiss"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LockedBanner;